import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { postAnswer } from "../../api/answer";
import { getQuizById, getQuizIdsByScenario } from "../../api/quiz";
import { getTexts } from "../../api/text";
import ScenarioBar from "../../components/ScenarioBar";
import { useTimer } from "../../hooks/useTimer";
import { setGameTexts, updateScore, updateTime } from "../../redux/actions";
import { useLang } from "../../translations/i18n";
import { QUIZZES, ROUTES, SCENARIOS, SCORE_TYPES } from "../../utils/constants";
import {
	getScenarioByScreen,
	getTimer,
	stripHtml,
	updateStats,
} from "../../utils/helpers";
import { alertError, modalQuestion5Feedback } from "../../utils/logger";

const CORRECT_ANSWER_POINTS = 20;
const WRONG_ANSWER_POINTS = -10;

const QUIZ_ID = QUIZZES.SCENARIO5;
const SCENARIO_ID = SCENARIOS.SCENARIO_5;
const NAVIGATE_TO = ROUTES.SCENARIO5_RESULT;

function shuffleArray(arr) {
	const a = [...arr];
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[a[i], a[j]] = [a[j], a[i]];
	}
	return a;
}

function Scenario5({ intl }) {
	const lang = useLang();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	const participant = useSelector((state) => state.group);
	const cachedQuiz = useSelector((state) => state.quizCache?.[SCENARIO_ID]);
	const scenarioNum = getScenarioByScreen(location.pathname);

	const [isCompleted, setIsCompleted] = useState(false);

	const [points, setPoints] = useState({ score: 0, penalty: 0 });
	const [time, setTime] = useState(0);

	const [options, setOptions] = useState([]);
	const [question, setQuestion] = useState("");
	const [footer, setFooter] = useState("");
	const [resolvedQuizId, setResolvedQuizId] = useState(null);

	useEffect(() => {
		const gameId = participant?.game;
		if (!gameId) return;
		getTexts({ game: gameId })
			.then((res) => {
				if (res.status !== 200 || !Array.isArray(res.data)) return;
				const map = res.data.reduce((acc, t) => ({ ...acc, [t.key]: t.value }), {});
				const q = map["scenario5.question"];
				const f = map["scenario5.footer"];
				if (q != null && stripHtml(String(q)).trim() !== "") setQuestion(q);
				if (f != null && stripHtml(String(f)).trim() !== "") setFooter(f);
				// Llenar caché global para que usePreloadGameAssets precargue imágenes (p. ej. scenario5.2)
				dispatch(setGameTexts(gameId, map));
			})
			.catch(() => {});
	}, [participant?.game, dispatch]);

	useEffect(() => {
		const gameId = participant?.game;
		if (!gameId) return;

		if (cachedQuiz?.data?.options?.length > 0) {
			setResolvedQuizId(cachedQuiz.resolvedQuizId || null);
			const mapped = cachedQuiz.data.options.map((o) => ({
				id: o.id,
				text: o.text || "",
				selected: false,
				correct: Boolean(o.correct),
				content: o.explanation || "",
			}));
			setOptions(shuffleArray(mapped));
			return;
		}

		const params = { game: gameId };
		getQuizIdsByScenario(SCENARIO_ID, params)
			.then((listRes) => {
				const list = listRes?.data && Array.isArray(listRes.data) ? listRes.data : [];
				const id = list[0]?._id || QUIZ_ID;
				setResolvedQuizId(id);
				return getQuizById(id);
			})
			.then((res) => {
				if (res?.status !== 200 || !res.data?.options?.length) return;
				const mapped = res.data.options.map((o) => ({
					id: o.id,
					text: o.text || "",
					selected: false,
					correct: Boolean(o.correct),
					content: o.explanation || "",
				}));
				setOptions(shuffleArray(mapped));
			})
			.catch(() => {});
	}, [participant?.game]);

	useTimer(
		() => {
			setTime(time + 1);
		},
		isCompleted ? null : 1000
	);

	const SelectOption = ({ id, index }) => {
		let option = options.find((x) => x.id === id);
		const letter = String.fromCharCode(97 + index) + ")";

		function handleOptionClick() {
			if (isCompleted) return;

			let updatedOptions = options.map((x) =>
				x.id === id
					? { ...x, selected: !x?.selected }
					: { ...x, selected: false }
			);

			setOptions(updatedOptions);
		}

		function getBackgroundColor() {
			return option?.selected && option?.correct && isCompleted
				? "#D6D431"
				: option?.selected && !option?.correct && isCompleted
				? "#FF5948"
				: option?.selected
				? "#EAF886"
				: "#FFFFFF";
		}

		return (
			<div>
				<div
					className={`d-flex flex-column align-items-center justify-content-center txt-dark-green mt-3 px-2 ${
						option?.selected ? "option-selected" : ""
					}`}
					style={{
						height: "18vh",
						lineHeight: "20px",
						borderRadius: "10px",
						backgroundColor: getBackgroundColor(),
					}}
					onClick={handleOptionClick}
				>
					<div className="row">
						<div className="col-1">
							<div className="bold">{letter}</div>
						</div>
						<div className="col-11 ps-0" dangerouslySetInnerHTML={{ __html: option?.text || "" }} />
					</div>
				</div>
			</div>
		);
	};

	function checkAnswers() {
		if (!options?.some((x) => x.selected)) {
			alertError({
				error: " ",
				customMessage:
					"Para avanzar, debes escoger al menos una opción.",
			});
			return;
		}

		const correct = options?.some((x) => x.selected && x.correct);

		setIsCompleted(correct);

		const score = correct ? CORRECT_ANSWER_POINTS : WRONG_ANSWER_POINTS;

		setPoints({
			...points,
			score: points?.score + score,
			penalty: points?.penalty + WRONG_ANSWER_POINTS,
		});

		const text = correct
			? `<div class="text-center">
					<div class="bold txt-turquoise">¡Correcto!</div>
					<div class="txt-black my-4">
						Estos son los criterios del documento de consenso GLOSEN
						2023<sup>1</sup> para la fase inicial o de inducción de debut o
						recaída de nefritis lúpica:
					</div>
				</div>`
			: `<div class="text-center">
					<div class="bold txt-turquoise">
						No es la respuesta correcta.
					</div>
					<div class="txt-black my-3">Vuelve a intentarlo.</div>
				</div>`;

		modalQuestion5Feedback({
			text: text,
			correct: correct,
			points: score,
		});
	}

	function submitAnswer() {
		let totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));
		postAnswer({
			scenario: SCENARIO_ID,
			quiz: resolvedQuizId ?? QUIZ_ID,
			group: participant?._id,
			score: points.score,
			time: time,
			correct: true,
		})
			.then((res) => {
				if (res.status === 201) {
					dispatch(updateScore(points.score, SCORE_TYPES.SCORE));
					dispatch(updateScore(points.penalty, SCORE_TYPES.PENALTY));

					updateStats({
						...participant,
						time: totalTime,
						score: participant?.score + points.score,
						penaltyScore:
							participant?.penaltyScore + points.penalty,
						lastVisited: NAVIGATE_TO,
					});
					navigate(NAVIGATE_TO, {
						state: { activeScenario: scenarioNum + 1 },
					});
				}
			})
			.catch((error) => {});
	}

	const questionDisplay = question ?? "";
	const footerDisplay = footer ?? "";
	const questionHasHtml = questionDisplay.indexOf("<") >= 0;

	return (
		<div className="h-100 bg-prueba">
			<div className="container" style={{ paddingTop: "2vh" }}>
				<div className="row">
					<div className="col-12 text-center px-3" style={{ minHeight: "80px", marginBottom: "16px" }}>
						<p className="txtWhite txt20 bold mb-0" style={{ lineHeight: 1.35 }}>
							{questionHasHtml ? (
								<span dangerouslySetInnerHTML={{ __html: questionDisplay }} />
							) : (
								questionDisplay
							)}
						</p>
					</div>
				</div>
				<div className="row justify-content-center">
					<div className="col-12">
						<div className="row">
							{options.map((option, index) => (
								<div key={option.id} className="col-6">
									<SelectOption id={option.id} index={index} />
								</div>
							))}
							<div className="col-12">
								<div
									className="txt8 txt-white w-50 mt-3"
									style={{ lineHeight: "15px" }}
								>
									{questionHasHtml ? (
								<span dangerouslySetInnerHTML={{ __html: footerDisplay }} />
							) : (
								footerDisplay
							)}

								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div
				className="container"
				style={{
					position: "absolute",
					bottom: "13vh",
					zIndex: 1000,
				}}
			>
				<div className="d-flex justify-content-end">
					<div
						className="btn-main"
						onClick={() => {
							if (isCompleted) submitAnswer();
							else checkAnswers();
						}}
					>
						<div className="text-btn-main">AVANZAR</div>
					</div>
				</div>
			</div>
			<ScenarioBar activeScenario={scenarioNum}></ScenarioBar>
		</div>
	);
}

export default injectIntl(Scenario5);
